type UnknownRecord = Record<string, unknown>;

const SAFE_DETAIL_KEYS = [
  'provider',
  'provider_reason_code',
  'correlation_id',
  'result_class',
] as const;

export type ManualPreCallFailureKind =
  | 'business_denial'
  | 'business_retry'
  | 'technical_failure'
  | 'timeout'
  | 'breaker_open'
  | 'unexpected_error';

export interface ManualCallLaunchPreCallError {
  errorCode: 'pre_call_skipped' | 'pre_call_retry_required';
  reasonCode: string | null;
  retryAfterSeconds: number | null;
  executionId: string | null;
  failureKind: ManualPreCallFailureKind;
  details: Partial<Record<(typeof SAFE_DETAIL_KEYS)[number], string>>;
}

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === 'object'
    ? value as UnknownRecord
    : null;
}

function normalizedString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized || null;
}

/**
 * Parse only the safe, snake_case pre-call failure contract returned inside
 * an Axios error. Raw script output and unknown detail fields are discarded.
 */
export function parseManualCallLaunchPreCallError(
  error: unknown,
): ManualCallLaunchPreCallError | null {
  const errorRecord = asRecord(error);
  const response = asRecord(errorRecord?.response);
  const data = asRecord(response?.data);
  const errorCode = normalizedString(data?.error);
  if (errorCode !== 'pre_call_skipped' && errorCode !== 'pre_call_retry_required') {
    return null;
  }

  const rawRetry = data?.retry_after_seconds;
  const parsedRetry = typeof rawRetry === 'number' ? rawRetry : Number(rawRetry);
  const retryAfterSeconds = Number.isFinite(parsedRetry) && parsedRetry >= 0
    ? parsedRetry
    : null;
  const rawFailureKind = normalizedString(data?.failure_kind);
  const allowedFailureKinds: ManualPreCallFailureKind[] = [
    'business_denial',
    'business_retry',
    'technical_failure',
    'timeout',
    'breaker_open',
    'unexpected_error',
  ];
  const failureKind = allowedFailureKinds.includes(rawFailureKind as ManualPreCallFailureKind)
    ? rawFailureKind as ManualPreCallFailureKind
    : errorCode === 'pre_call_retry_required'
      ? 'business_retry'
      : 'business_denial';

  const detailsRecord = asRecord(data?.details);
  const details: ManualCallLaunchPreCallError['details'] = {};
  SAFE_DETAIL_KEYS.forEach((key) => {
    const value = normalizedString(detailsRecord?.[key]);
    if (value) details[key] = value;
  });

  return {
    errorCode,
    reasonCode: normalizedString(data?.reason_code),
    retryAfterSeconds,
    executionId: normalizedString(data?.pre_call_execution_id),
    failureKind,
    details,
  };
}
